/**
 * English message bundle. All other locales mirror this shape; tsc enforces
 * key parity via `Messages = typeof en` in `../index.ts`.
 *
 * Keep keys flat and namespaced (page or component prefix). Placeholders use
 * `{name}` syntax — see `t()` for substitution rules.
 *
 * Translator constraint: most strings render as text (textContent / confirm /
 * showToast) and accept any character. EXCEPTIONS that get interpolated into
 * raw HTML — these MUST NOT contain `"`, `<`, `>`, or `&`:
 *   - Anything ending in `_aria` (used inside `aria-label="..."` attributes)
 *   - `app.version_free`, `gallery.open_design`, `gallery.categories_label`,
 *     `gallery.jump_label`, `gallery.empty_headline`, `viewer.back_to_gallery`,
 *     `viewer.meta_*`, `viewer.swap_note`, `viewer.open_in_design`,
 *     `viewer.export_pdf`, `viewer.export_png`, `viewer.copy_link`,
 *     `design.title`, `design.board_label`, `design.zoom_label`,
 *     `design.tool_label`, `design.legend_toggle_label`, `design.undo`,
 *     `design.redo`, `design.hint_local_only`, `quantize.title`,
 *     `quantize.drop_label_*`, `quantize.drop_hint`, `quantize.board_label`,
 *     `quantize.colors_label`, `quantize.preview_hint`, `quantize.cancel`,
 *     `quantize.confirm`, `design.export.default_name`
 *
 * Keys ending in `_html` deliberately contain markup — see
 * `gallery.empty_sub_html`, `design.mirror_h_html`, `design.mirror_v_html`.
 */
export const en = {
  // App-level / document chrome
  "app.title": "Bead Blueprint",
  "app.description":
    "Free, ad-free bead blueprints. Browse a gallery, or design your own.",
  "app.version_free": "v0.1 · free",
  "app.tip_jar": "Tip jar 🍕",
  "app.tip_jar_aria": "Send a tip",
  "app.lang_label": "Language",
  "app.lang_english": "English",
  "app.lang_icelandic": "Íslenska",

  // Gallery
  "gallery.title": "Bead Blueprint.",
  "gallery.count_one": "1 pattern",
  "gallery.count_many": "{count} patterns",
  "gallery.open_design": "Design →",
  "gallery.open_design_aria":
    "Open the design page to draw or generate from a photo",
  "gallery.categories_label": "Categories",
  "gallery.categories_aria": "Categories",
  "gallery.jump_label": "Categories",
  "gallery.jump_aria": "Filter by category",
  "gallery.category_filter_all": "All categories",
  "gallery.body_aria": "Blueprint gallery",
  "gallery.menu_aria": "More options",
  "gallery.menu_close_aria": "Close menu",
  "gallery.empty_headline": "No patterns yet.",
  "gallery.empty_sub_html":
    "Drop a TS file under <code>src/blueprints/&lt;category&gt;/</code> and reload.",
  "gallery.empty_filter_match": "No patterns match this filter.",
  "gallery.filter_label": "Colors",
  "gallery.filter_aria": "Filter by color count",
  "gallery.filter_all": "All",
  "gallery.filter_one": "1 color",
  "gallery.filter_two_three": "2-3 colors",
  "gallery.filter_four_plus": "4+ colors",

  // Categories
  "category.animals": "Animals",
  "category.people": "People",
  "category.vehicles": "Vehicles",
  "category.food": "Food",
  "category.nature": "Nature",
  "category.space": "Space",
  "category.letters": "Letters",
  "category.alphabet": "Alphabet",
  "category.numbers": "Numbers",
  "category.holidays": "Holidays",
  "category.emoji": "Emoji",
  "category.shapes": "Shapes",
  "category.other": "Other",

  // Viewer
  "viewer.title_format": "{name}.",
  "viewer.back_to_gallery_aria": "Back to gallery",
  "viewer.back_to_gallery": "← Gallery",
  "viewer.meta_aria": "Blueprint metadata",
  "viewer.meta_board": "Board",
  "viewer.meta_category": "Category",
  "viewer.meta_beads": "Beads",
  "viewer.meta_authored": "Authored",
  "viewer.swap_note": "Tap any color in the bead list to swap.",
  "viewer.preview_aria": "Pattern preview",
  "viewer.grid_aria": "Bead pattern preview",
  "viewer.legend_aria": "Bead list",
  "viewer.open_in_design": "Open in Design",
  "viewer.open_in_design_aria": "Open this pattern in Design",
  "viewer.export_pdf": "Export PDF",
  "viewer.export_png": "Export PNG",
  "viewer.copy_link": "Copy link",
  "viewer.copy_link_done": "Copied",
  "viewer.exporting": "Exporting…",
  "viewer.rendering": "Rendering…",
  "viewer.chip_color_one": "{board} · 1 color",
  "viewer.chip_color_many": "{board} · {count} colors",
  "viewer.authored_manual": "Hand · manual",
  "viewer.toast_not_found": "Pattern not found.",
  "viewer.toast_pdf_failed": "PDF export failed — try again.",
  "viewer.toast_png_failed": "PNG export failed — try again.",
  "viewer.toast_link_copied": "Link copied.",
  "viewer.copy_prompt": "Copy this URL:",
  "viewer.shared_pattern_name": "Shared pattern",
  "viewer.toast_share_invalid": "This shared link can't be opened — it may be from a newer version.",
  "viewer.downscale_note": "Downscaled from {native} — sampling is unpredictable, check the result before printing.",
  "viewer.downscale_note_fragmented": "Downscaled from {native} into {components} disconnected pieces — they won't fuse into one when ironed. Try a larger size.",
  "viewer.board_native_aria": "{name} (original size)",

  // Design — chrome
  "design.app_aria": "Bead Blueprint",
  "design.title": "Design.",
  "design.controls_aria": "Controls",
  "design.board_picker_aria": "Board picker",
  "design.board_label": "Board",
  "design.board_shape_aria": "Board shape",
  "design.zoom_label": "Zoom",
  "design.zoom_aria": "Zoom",
  "design.zoom_level_aria": "Zoom level",
  "design.tools_aria": "Tools",
  "design.tool_label": "Tool",
  "design.tool_aria": "Tool",
  "design.active_color_aria": "Active color",
  "design.recent_colors_aria": "Recent colors",
  "design.legend_toggle_label": "Colors used",
  "design.legend_aria": "Bead list",
  "design.history_aria": "History",
  "design.undo": "Undo",
  "design.redo": "Redo",
  "design.canvas_aria": "Canvas",
  "design.hint_local_only":
    "Drafts live in this browser only — export to share.",
  "design.pattern_info_aria": "Pattern info",
  "design.design_tools_aria": "Design tools",
  "design.setup_aria": "Setup",
  "design.canvas_label":
    "Bead canvas. Use arrow keys to move the cursor, Enter to paint, Backspace to erase.",

  // Design — tools, zoom
  "design.tool.paint": "Paint",
  "design.tool.fill": "Fill",
  "design.tool.erase": "Erase",
  "design.zoom.fit": "Fit",
  "design.zoom.1x": "1×",
  "design.zoom.2x": "2×",
  "design.zoom.3x": "3×",
  "design.zoom.4x": "4×",

  // Design — mirror toggles + icons
  "design.mirror_h_html":
    '<span class="design-toggle-text">Mirror </span><span class="design-toggle-glyph">↔</span>',
  "design.mirror_v_html":
    '<span class="design-toggle-text">Mirror </span><span class="design-toggle-glyph">↕</span>',
  "design.mirror_h_icon_label": "Mirror horizontal",
  "design.mirror_v_icon_label": "Mirror vertical",

  // Design — export menu
  "design.export.trigger": "Export",
  "design.export.aria": "Export pattern",
  "design.export.share_community": "Share with community",
  "design.export.pdf": "PDF — printable beadmap",
  "design.export.png": "PNG — image",
  "design.export.json": "Blueprint (JSON)",
  "design.export.json_copy": "Copy blueprint JSON",
  "design.export.copy_share_link": "Copy share link",

  // Design — import / new menu
  "design.import.trigger": "New",
  "design.import.aria": "New pattern",
  "design.import.blank": "Blank board",
  "design.import.from_blueprint": "From blueprint (JSON)…",
  "design.import.from_image": "From image…",
  "design.import.adjust_image": "Adjust image…",

  // Design — toasts + confirms
  "design.toast.pattern_not_found": 'Pattern "{id}" not found.',
  "design.toast.draft_save_failed":
    "Drafts can't be saved in this browser mode — your work won't persist.",
  "design.toast.board_cleared": "Board cleared.",
  "design.toast.board_changed": "Board: {name}",
  "design.toast.board_scaled": "Scaled to {name}",
  "design.toast.draft_incompatible":
    "Saved draft was incompatible — starting fresh.",
  "design.toast.loaded_seed": "Loaded {name} — keep editing.",
  "design.toast.pdf_exported_phone": "PDF exported — check Files or open tabs",
  "design.toast.pdf_failed": "PDF export failed — try again.",
  "design.toast.png_exported_phone": "PNG exported — check Files or open tabs",
  "design.toast.png_failed": "PNG export failed — try again.",
  "design.toast.json_exported": "Exported {filename}",
  "design.toast.json_copied": "Blueprint JSON copied to clipboard",
  "design.toast.json_copy_failed": "Could not copy to clipboard.",
  "design.toast.link_copied": "Share link copied.",
  "design.toast.link_copy_failed": "Could not copy link — try Export → JSON.",
  "design.toast.share_invalid": "This shared link can't be opened — it may be from a newer version.",
  "design.toast.share_pattern_too_large": "This pattern is too detailed to share by URL — export PNG/PDF instead.",
  "design.toast.share_submitted": "Submitted for review.",
  "design.toast.share_already_submitted": "Already sent for review — no need to resubmit.",
  "design.toast.share_submit_failed": "Couldn't submit — try again.",
  "design.copy_json_prompt": "Copy this blueprint JSON:",
  "design.export.default_name": "Untitled",
  "design.toast.read_failed": "Could not read file.",
  "design.toast.import_failed": "Import failed: {error}",
  "design.toast.imported_one": 'Imported "{name}"',
  "design.toast.imported_with_warnings_one":
    'Imported "{name}" (with 1 warning)',
  "design.toast.imported_with_warnings_many":
    'Imported "{name}" (with {count} warnings)',
  "design.toast.imported_from_image":
    "Imported from image — paint over to refine.",
  "design.toast.image_unreadable":
    "Could not read this image — try another file.",
  "design.toast.drop_help": "Drop a .bead.json file or an image to start.",
  "design.confirm.replace_with_seed":
    'Load "{name}"? Your current draft will be replaced.',
  "design.confirm.switch_board_title": "Switch to {name}?",
  "design.confirm.switch_board_scale":
    "Scale your current design to fit {name}, or start fresh? Undo will restore your previous design either way.",
  "design.confirm.switch_board_downscale":
    "{name} has fewer beads — scaling will drop detail. Undo will restore your full-resolution original.",
  "design.confirm.switch_board_aspect_mismatch":
    "{name} has a different aspect ratio — scaling would distort your design. Start fresh? Undo will restore your previous design.",
  "design.confirm.scale_to_fit": "Scale to fit",
  "design.confirm.start_fresh": "Start fresh",
  "design.confirm.cancel": "Cancel",
  "design.confirm.continue": "Continue",
  "design.confirm.share_after_export_title": "Share with community?",
  "design.confirm.share_after_export_message":
    "A person reviews it manually before it's added to the public gallery.",
  "design.confirm.share_after_export_confirm": "Share",
  "design.confirm.share_after_export_cancel": "No thanks",
  "design.confirm.replace_draft": "Replace draft",
  "design.confirm.replace_with_seed_title": "Replace draft?",
  "design.confirm.import_replaces_draft_title": "Replace draft?",
  "design.confirm.image_replaces_draft_title": "Replace draft?",
  "design.confirm.import_replaces_draft":
    'Importing "{name}" will replace your current draft. Continue?',
  "design.confirm.image_replaces_draft":
    "Generating from image will replace your current draft. Continue?",

  // Design — accessibility live announcer
  "design.aria.tool_announce": "Tool: {tool}",
  "design.aria.zoom_announce": "Zoom: {name}",
  "design.aria.cell_announce": "Cell {x}, {y}",
  "design.aria.painted_at": "Painted {color} at cell {x}, {y}",
  "design.aria.erased_at": "Erased at cell {x}, {y}",
  "design.aria.unknown_color": "unknown",
  "design.aria.switch_to_color": "Switch to {name}",

  // Phone setup sheet
  "phone_setup.aria": "Setup",
  "phone_setup.import": "Import…",
  "phone_setup.from_image": "From image…",
  "phone_setup.clear": "Clear board",
  "phone_setup.gallery": "← Back to gallery",

  // Quantize modal
  "quantize.title": "Generate from image",
  "quantize.aria": "Generate from image",
  "quantize.drop_aria": "Drop an image to start",
  "quantize.drop_label_cursor": "Drop image",
  "quantize.drop_label_touch": "Tap to choose",
  "quantize.drop_hint": "PNG · JPEG · WebP",
  "quantize.board_label": "Board",
  "quantize.colors_label": "Colors",
  "quantize.colors_aria": "Maximum color count",
  "quantize.color_cap.all": "All",
  "quantize.preview_hint": "Drop an image to see a preview.",
  "quantize.legend_aria": "Bead list",
  "quantize.cancel": "Cancel",
  "quantize.confirm": "Use this pattern",
  "quantize.toast.decoding": "Decoding {filename}…",
  "quantize.toast.image_unreadable":
    "Could not read this image — try another file.",
  "quantize.toast.processing_failed":
    "Image processing failed — try a different image.",
  "quantize.portrait_nudge":
    "Looks like a portrait? Faces come out clearer on a 58×58 board.",
  "quantize.portrait_nudge_use_2x2": "Use 58×58",
  "quantize.portrait_nudge_dismiss": "Keep current",

  // Legend / colors-used panel
  "legend.colors_used_aria": "Colors used",
  "legend.row_aria_one": "{name}, 1 bead. Click to swap color.",
  "legend.row_aria_many": "{name}, {count} beads. Click to swap color.",

  // Color pickers
  "color_picker.aria": "Pick a color",
  "color_picker.head": "Pick a color",
  "swap_picker.aria": "Pick a replacement color",
  "swap_picker.head": "Swap {name} →",

  // Generic UI
  "ui.actions_aria": "Actions",
  "ui.options_aria": "Options",

  // Validation
  "validation.no_file": "No file provided",
  "validation.heic":
    "iPhone photos: open in Photos and export as JPEG, then drop here.",
  "validation.unsupported": "Drop an image — PNG, JPEG, or WebP.",
  "validation.too_large": "Image too large — try under 25 MB.",

  // Privacy page
  "privacy.link": "Privacy",
  "privacy.link_aria": "Privacy policy",
  "privacy.title": "Privacy.",
  "privacy.back": "← Gallery",
  "privacy.back_aria": "Back to gallery",
  "privacy.updated": "Last updated 2026-08-04",

  "privacy.tldr_label": "Tl;dr",
  "privacy.tldr_body":
    "Bead Blueprint runs entirely in your browser. We don't have user accounts, we don't upload your images, and we don't sell anything about you. The only data leaving your device is anonymous traffic analytics, an optional tip payment, and — only if you choose to share your design with the community — the pattern you're submitting for gallery review.",

  "privacy.section_data_label": "What we collect",
  "privacy.data_images_head": "Images you drop in",
  "privacy.data_images_body":
    "Images you drop into the generator are decoded and processed locally in your browser. They are never sent to a server we control and we cannot see them.",
  "privacy.data_drafts_head": "Drafts and preferences",
  "privacy.data_drafts_body":
    "Your in-progress design and your language preference are saved to your browser's localStorage so they survive a refresh. They stay on your device — clearing site data removes them.",
  "privacy.data_analytics_head": "Anonymous traffic analytics",
  "privacy.data_analytics_body":
    "We use Cloudflare Web Analytics to count page views and see which patterns are popular. It is cookieless, does not fingerprint visitors, and does not track you across sites. Cloudflare briefly processes your IP and user-agent to filter bots; no personal data is stored or sold.",
  "privacy.data_tips_head": "Tips (optional)",
  "privacy.data_tips_body":
    "If you choose to leave a tip, the checkout link opens Polar.sh — our payment processor. They handle your name, email, and card details under their own privacy policy. We only receive a payout summary; we never see your card number.",
  "privacy.data_submissions_head": "Community gallery submissions (optional)",
  "privacy.data_submissions_body":
    "If you choose \"Share with community\" from the Export menu — or say yes when asked after another export — we send just the pattern itself, not any photo it was based on, to our server for review. We store only the pattern, a timestamp, and a review status: no name, note, IP address, or other identifying information. There's no way to trace a submission back to you. A person reviews it manually; nothing is added to the public gallery automatically.",

  "privacy.section_dont_label": "What we don't do",
  "privacy.dont_accounts": "We don't have user accounts or logins.",
  "privacy.dont_upload":
    "We don't upload, store, or share your images — the only thing we ever store is a submitted design's pattern, and only if you opt in.",
  "privacy.dont_sell": "We don't sell or share data with advertisers.",
  "privacy.dont_track":
    "We don't use tracking cookies or cross-site identifiers.",
  "privacy.dont_identify_submissions":
    "We don't collect anything that could identify who submitted a design to the gallery.",

  "privacy.section_third_label": "Third parties",
  "privacy.third_cloudflare": "Cloudflare — hosting + cookieless analytics.",
  "privacy.third_polar": "Polar.sh — tip jar payments (only if you tip).",
  "privacy.third_fonts": "Google Fonts — serves the typefaces used by the UI.",

  "privacy.section_rights_label": "Your rights",
  "privacy.rights_body":
    "Because we don't store personal data on our side, there's nothing for us to look up, export, or delete. You can clear local drafts at any time via your browser's site settings. Tip-related questions go to the payment processor (Polar.sh).",

  "privacy.section_contact_label": "Contact",
  "privacy.contact_body":
    "Questions about this policy? Email birkirb@outlook.com.",

  // Built-in blueprint names — keyed by Blueprint.id. blueprintName() falls
  // back to the runtime `name` field for user-imported ids not listed here.
  "blueprint_name.0": "0",
  "blueprint_name.1": "1",
  "blueprint_name.2": "2",
  "blueprint_name.3": "3",
  "blueprint_name.4": "4",
  "blueprint_name.5": "5",
  "blueprint_name.6": "6",
  "blueprint_name.7": "7",
  "blueprint_name.8": "8",
  "blueprint_name.9": "9",
  "blueprint_name.airplane": "Airplane",
  "blueprint_name.alien": "Alien",
  "blueprint_name.ambulance": "Ambulance",
  "blueprint_name.astronaut": "Astronaut",
  "blueprint_name.bee": "Bee",
  "blueprint_name.bunny": "Bunny",
  "blueprint_name.bus": "Bus",
  "blueprint_name.car": "Car",
  "blueprint_name.cat": "Cat",
  "blueprint_name.christmas-tree": "Christmas Tree",
  "blueprint_name.circle": "Circle",
  "blueprint_name.cloud": "Cloud",
  "blueprint_name.crab": "Crab",
  "blueprint_name.cupcake": "Cupcake",
  "blueprint_name.dog": "Dog",
  "blueprint_name.donut": "Donut",
  "blueprint_name.elephant": "Elephant",
  "blueprint_name.fire-truck": "Fire truck",
  "blueprint_name.fish": "Fish",
  "blueprint_name.flower": "Flower",
  "blueprint_name.fox": "Fox",
  "blueprint_name.frog": "Frog",
  "blueprint_name.girl": "Girl",
  "blueprint_name.heart": "Heart",
  "blueprint_name.helicopter": "Helicopter",
  "blueprint_name.ice-cream": "Ice Cream",
  "blueprint_name.ladybug": "Ladybug",
  "blueprint_name.lightning": "Lightning",
  // The id `mars-rocer` is a typo'd legacy id — keep it stable, label it correctly.
  "blueprint_name.mars-rocer": "Mars Rover",
  "blueprint_name.monkey": "Monkey",
  "blueprint_name.moon": "Moon",
  "blueprint_name.mountains": "Mountains",
  "blueprint_name.mushroom": "Mushroom",
  "blueprint_name.music-note": "Music Note",
  "blueprint_name.nut": "Nut",
  "blueprint_name.owl": "Owl",
  "blueprint_name.panda": "Panda",
  "blueprint_name.penguin": "Penguin",
  "blueprint_name.pickup": "Pickup",
  "blueprint_name.pizza": "Pizza",
  "blueprint_name.planet": "Planet",
  "blueprint_name.plus": "Plus",
  "blueprint_name.police-car": "Police car",
  "blueprint_name.pumpkin": "Pumpkin",
  "blueprint_name.rainbow": "Rainbow",
  "blueprint_name.rocket": "Rocket",
  "blueprint_name.sailboat": "Sailboat",
  "blueprint_name.shamrock": "Shamrock",
  "blueprint_name.shark": "Shark",
  "blueprint_name.side-profile-beagle": "Side Profile Beagle",
  "blueprint_name.side-profile-dachshund": "Side Profile Dachshund",
  "blueprint_name.side-profile-husky": "Side Profile Husky",
  "blueprint_name.smiley": "Smiley",
  "blueprint_name.snake": "Snake",
  "blueprint_name.snowflake": "Snowflake",
  "blueprint_name.star": "Star",
  "blueprint_name.submarine": "Submarine",
  "blueprint_name.sun": "Sun",
  "blueprint_name.train": "Train",
  "blueprint_name.tree": "Tree",
  "blueprint_name.turtle": "Turtle",
  "blueprint_name.ufo": "UFO",
  "blueprint_name.volcano": "Volcano",
  "blueprint_name.watermelon": "Watermelon",
  "blueprint_name.whale": "Whale",
  "blueprint_name.x": "X",
} as const;
